import type { WorkspaceTaskSummary } from '@/features/tasks';
import type { TaskExecutionSummary, TaskView } from '@/lib/api/generated/model';

/**
 * What happened since the person last left (spec 155 US1), from what the
 * Overview already holds. Pure, so the counting is tested on its own.
 *
 * - Runs finished: agent runs that ended after `since`.
 * - Decisions arrived: Your turn's decisions that started waiting after it.
 * - Went wrong: Your turn's rows that went wrong after it.
 */

export interface SinceSummary {
  runsFinished: number;
  decisionsArrived: number;
  wentWrong: number;
  /** The latest counted event, for "anything newer than the dismissal". */
  newest: number | null;
}

const FINISHED = new Set<TaskExecutionSummary['status']>([
  'completed',
  'failed',
  'stopped',
  'blocked',
]);

export function sinceSummary(
  attention: WorkspaceTaskSummary['attention'],
  tasks: readonly TaskView[],
  since: number,
): SinceSummary {
  let newest: number | null = null;
  const note = (at: number) => {
    newest = newest === null ? at : Math.max(newest, at);
  };
  const after = (at: string | null | undefined): number | null => {
    if (!at) return null;
    const time = Date.parse(at);
    return Number.isFinite(time) && time > since ? time : null;
  };

  let runsFinished = 0;
  for (const task of tasks) {
    for (const run of task.executions ?? []) {
      if (run.step_kind_snapshot !== 'ai' || !FINISHED.has(run.status)) {
        continue;
      }
      const at = after(run.ended_at);
      if (at === null) continue;
      runsFinished += 1;
      note(at);
    }
  }

  const countRows = (rows: readonly { enteredAt: string | null }[]) => {
    let count = 0;
    for (const row of rows) {
      const at = after(row.enteredAt);
      if (at === null) continue;
      count += 1;
      note(at);
    }
    return count;
  };

  return {
    runsFinished,
    decisionsArrived: countRows(attention.waiting),
    wentWrong: countRows(attention.needsAttention),
    newest,
  };
}

/** An absence shorter than this is not "away" (FR-002). */
export const AWAY_MS = 15 * 60 * 1000;

/** "at 17:40", "yesterday at 17:40", or "on 9 Oct at 17:40", in local time. */
export function whenWords(at: number, now: number): string {
  const time = new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(at);
  const day = (value: number) => new Date(value).toDateString();
  if (day(at) === day(now)) return `at ${time}`;
  if (day(at) === day(now - 24 * 60 * 60 * 1000)) {
    return `yesterday at ${time}`;
  }
  const date = new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
  }).format(at);
  return `on ${date} at ${time}`;
}
